/** @file       Provide a standard set of DCP CLI options and related utility functions via `yargs`.
 *  @author     Eddie Roosenmaallen, eddie@kingsds.network
 *  @date       March 2020
 *  
 * @module     dcp/cli
 * @access     public
 *
 * @deprecated DO NOT USE THIS MODULE /wg Jun 2025
 */

let utilityName = 'DCP Commandline Utility';

/** 
 * Generate a {@link https://yargs.js.org/docs/|yargs} object with some DCP options provided and hidden.
 * To see these options pass `--help --show-hidden`.
 * 
 * ```
 * DCP Common Options
 * --identity, -i               Identity keystore name
 * --identity-file, -I          Identity keystore file (overrides --identity)
 *                                         [string] [default: ~/.dcp/id.keystore]
 * --default-bank-account       Default Bank account keystore name
 * --default-bank-account-file  Default Bank account keystore file path
 *                              (overrides --default-bank-account)
 *                                    [string] [default: ~/.dcp/default.keystore]
 * --dcpConfig                  Alternate DCP configuration file to load [string]
 * --scheduler                  Specify scheduler to connect to/fetch dcp-client
 *                              from
 *                           [default: "https://scheduler.distributed.computer/"]
 * ```
 * 
 *  @access public
 *  @example <caption>Chain yargs commands</caption>
 *  const { start, end } = dcpCli.base('description')
 *  .options({
 *    start: {
 *      describe: 'Positive integer',
 *      type: 'number',
 *      default: 1,
 *    },
 *    end: {
 *      describe: 'Positive integer',
 *      type: 'number',
 *      default: 2,
 *    }   
 *  }).argv;
 */
function base(description=utilityName) {
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const yargs = requireNative('yargs/yargs');
  const { hideBin } = requireNative('yargs/helpers');
  const process = requireNative('process');
  const path = requireNative('path');
  const scriptName = path.basename(process.argv0 || 'node');

  return yargs(hideBin(process.argv))
  .usage(`
$0 - ${description}
Copyright (c) 2020 Kings Distributed Systems Ltd., All Rights Reserved.

To view the common DCP options, use the --help flag with --dcp-options.`)
  .help('help', `Show help, use with commands to show detailed command help: ${scriptName} <cmd> --help`)
  .showHidden('show-hidden', 'Show hidden options, and DCP common options (must be used with the --help flag)')
  .alias('help', 'h')
  .alias('show-hidden', 'dcp-options')
  .hide('version')
  .option('identity', {
    description: 'Identity keystore name',
    alias: 'i',
    hidden: true,
  })
  .option('identity-file', {
    description: 'Identity keystore file (overrides --identity)',
    defaultDescription: '~/.dcp/id.keystore',
    normalize: true,
    alias: 'I',
    hidden: true,
  })
  .option('default-bank-account', {
    description: 'Default Bank account keystore name',
    hidden: true,
  })
  .option('default-bank-account-file', {
    description: 'Default Bank account keystore file path (overrides --default-bank-account)',
    defaultDescription: '~/.dcp/default.keystore',
    normalize: true,
    hidden: true,
  })
  .option('dcpConfig', {
    description: 'Alternate DCP configuration file to load',
    normalize: true,
    hidden: true,
  })
  .option('dcp-scheduler', {
    description:
      "Specify a scheduler's URL to connect to/fetch dcp-client from",
    type: 'string',
    requiresArg: true,
    hidden: false,
  })
  .coerce({
    dcpScheduler: (arg) => new URL(arg),
  })
  .group(
    [
      'identity',
      'identity-file',
      'default-bank-account',
      'default-bank-account-file',
      'dcpConfig',
      'dcp-scheduler',
    ],
    'DCP Common Options',
  )
  /**
   * Mainly to remove as many unnecessary properties from the parsed the `argv`
   * object as possible.
   */
  .parserConfiguration({
    'sort-commands': true,
    'strip-aliased': true,
    // Commented due to https://github.com/yargs/yargs/issues/2356. When this issue is resolved the option can be used again (rs)
    // 'strip-dashed': true,
  })
  /**
   * For some reason the docs say .wrap(yargs.terminalWidth()) works, but that's
   * incorrect. terminalWidth only exists once we call the function.
   * @see https://yargs.js.org/docs/#api-reference-wrapcolumns
   */
  .wrap(yargs().terminalWidth());
}

/** 
 *  Get the Identity keystore indicated by the cli options/DCP defaults.
 *  @note   As a side-effect, sets the Wallet module's Identity
 *  @return {module:dcp/wallet.IdKeystore} The user's configured Identity keystore
 *  @async
 *  @access public
 */
async function getIdentityKeystore() {
  const { identity, identityFile } = base().help(false).argv;
  const wallet = require('dcp/dcp-client/wallet');

  if(getIdentityKeystore.ks) {
    // We have already cached the ks.
    // Caching is important because if we load the same keystore several times,
    // the user may have to enter the same passphrase several times.
    return getIdentityKeystore.ks;
  }
  
  if (identityFile) {
    getIdentityKeystore.ks = wallet.load(identityFile).then(loaded => {
      if (!loaded.safe)
        console.warn(`Warning! The keystore file ${identityFile} has unsafe permissions!`);

      wallet.addId(loaded.keystore);
      return wallet.getId();
    });
  }
  else
    getIdentityKeystore.ks = await wallet.getId(identity);
  return getIdentityKeystore.ks;
}

/** 
 *  Default Account keystore as indicated by CLI options/DCP defaults
 *  @param {boolean} [hideWarning=false] suppress the warning about unsafe permissions
 *  @return {module:dcp/wallet.BankAccountKeystore} The user's configured Account keystore
 *  @async
 *  @access public
 */
function getAccountKeystore(hideWarning = false) {
  const { defaultBankAccount, defaultBankAccountFile } = base().help(false).argv;
  const wallet = require('dcp/dcp-client/wallet');
  
  if (defaultBankAccountFile) {
    return wallet.load(defaultBankAccountFile).then(loaded => {
      if (!loaded.safe && !hideWarning)
        console.warn(`Warning! The keystore file ${defaultBankAccountFile} has unsafe permissions!`);

      return loaded.keystore;
    });
  }
  else
    return wallet.get(defaultBankAccount);
}

/** @export name
 *  Get/set the name of the utility
 * @access public
 */
Object.defineProperty(module.exports, 'name', {
  get: () => utilityName,
  set: (n) => utilityName = n,
  enumerable: true,
  configurable: false,
});

/** @export argv
 *  Shortcut to get the base set of default arguments
 * @access public
 */
Object.defineProperty(module.exports, 'argv', {
  get: () => base().argv,
});

Object.assign(module.exports, {
  // name, - defined above
  // argv, - defined above
  base,
  getIdentityKeystore,
  getAccountKeystore,
});
